import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, Paper, Stepper, Step, StepLabel,
  Alert, Snackbar, Chip, Divider, MenuItem, Autocomplete, Tabs, Tab, CircularProgress,
  FormControl, InputLabel, Select, Tooltip, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupIcon from '@mui/icons-material/Group';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NerdSpinner from '@/components/NerdSpinner';
import PolarisAppIdInstructions from '@/components/PolarisAppIdInstructions';
import { fetchEntitlements, encryptString } from '@/lib/crypto';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import InlineProjectList from '@/app/project-settings/pages/components/InlineProjectList';
import InlineTrivyImageList from '@/app/project-settings/pages/components/InlineTrivyImageList';
import { extractBaseUrl, EditMappingsDialog } from '@/app/project-settings/pages/components';
import {
  fetchOnboardingDetail, createOnboarding, updateOnboarding, fetchGheBranches,
  validateGheConnection, validateSonarConnection, validateBlackDuckConnection, validateTrivyConnection,
  validatePolarisConnection,
  saveManagementAccessEntry, fetchManagementAccessEntries,
  type ValidationResult,
} from './api';
import { triggerScanPipeline } from '@/lib/api/pipelineService';
import { triggerAiScan } from '@/lib/api/aiKpiService';
import {
  buildYaml, EMPTY_FORM, deriveConfigName, parseYamlToForm,
  generateCiYaml, generateCicdYaml, generatePrYaml,
  type OnboardingFormData, type GheRepo, type MemberEntry, type ManagementAccessEntry,
} from './yamlBuilder';
import { isValidMBEmail, isValidAppId, normalizeAppId, parsePolarisApplicationId } from './validation';

const PROJECT_PHASES = ['PLANNING', 'ACTIVE_DEVELOPMENT', 'KTLO', 'MAINTENANCE', 'SUNSET'];
const CRITICALITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const DATA_CLASSIFICATION_OPTIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'STRICTLY_CONFIDENTIAL'];
const LANGUAGE_OPTIONS = ['Java', 'TypeScript', 'JavaScript', 'Python', 'Go', 'Kotlin', 'C#', 'C++', 'Rust'];
const FRAMEWORK_OPTIONS = ['Spring Boot', 'React', 'Angular', 'Vue', 'Next.js', 'Express', 'Django', 'Flask', '.NET'];
const BUILD_TOOL_OPTIONS = ['Gradle', 'Maven', 'Vite', 'Webpack', 'npm', 'Yarn', 'pnpm', 'Bazel'];
const COMPLIANCE_OPTIONS = ['ISO-27001', 'SOC-2', 'GDPR', 'PCI-DSS', 'HIPAA', 'TISAX'];
const REPO_CRITICALITY_OPTIONS = ['HIGH', 'MEDIUM', 'LOW'] as const;
const REPO_TYPE_OPTIONS = ['INFRA', 'NON_INFRA'] as const;
const ONBOARDING_MANAGEMENT_LEVELS = ['Management 1', 'Management 2', 'Management 3', 'Project SPOC'];

const errSx = (hasError: boolean) =>
  hasError ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'error.main', borderWidth: 2 } } } as const : undefined;

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>{children}</Box>
);

const ToolGroup: React.FC<{ icon: React.ReactNode; title: string; connected?: boolean; children: React.ReactNode }> = ({ icon, title, connected, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {icon}
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
      {connected && (
        <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Connected" size="small" color="success"
          sx={{ fontSize: 11, height: 22, ml: 0.5 }} />
      )}
    </Box>
    {children}
  </Paper>
);

const STEP_ICON_MAP: Record<number, React.ReactElement> = {
  1: <AccountTreeIcon />, 2: <DeviceHubIcon />, 3: <DeviceHubIcon />, 4: <GroupIcon />, 5: <FactCheckIcon />,
};

interface OnboardingWizardProps {
  mode: 'create' | 'edit';
  appId?: string;
  initialLeanIxId?: string;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ mode, appId, initialLeanIxId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<OnboardingFormData>({ ...EMPTY_FORM, leanIxId: initialLeanIxId ? normalizeAppId(initialLeanIxId) : 'APP-', spocs: [{ name: '', email: '' }], gheRepos: [{ url: '', branch: '', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' }] });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(mode === 'edit');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));
  const isTouched = (field: string) => !!touched[field];

  const [repoBranches, setRepoBranches] = useState<Record<string, string[]>>({});
  const [branchLoading, setBranchLoading] = useState<Record<string, boolean>>({});
  const fetchBranchDebounce = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  type ConnStatus = null | { loading: boolean; result: ValidationResult | null };
  const [connValidation, setConnValidation] = useState<{ ghe: ConnStatus; sonar: ConnStatus; blackduck: ConnStatus; trivy: ConnStatus; polaris: ConnStatus }>({ ghe: null, sonar: null, blackduck: null, trivy: null, polaris: null });
  const [ghePatVerified, setGhePatVerified] = useState(false);

  const [submitSuccess, setSubmitSuccess] = useState<{ name: string; appId: string; status: 'success' | 'partial' } | null>(null);
  const [snackMsg, setSnackMsg] = useState('');
  const [draftAppId, setDraftAppId] = useState<string | undefined>(appId);
  const [savingDraft, setSavingDraft] = useState(false);
  // Project key selection state (populated after token is saved to DynamoDB)
  const [sonarTokenSaved, setSonarTokenSaved] = useState(false);
  const [bdTokenSaved, setBdTokenSaved] = useState(false);
  const [trivyTokenSaved, setTrivyTokenSaved] = useState(false);
  const [polarisConfigSaved, setPolarisConfigSaved] = useState(false);
  // Stable temporary ID used to store tokens in DDB before the real project exists.
  // Allows InlineProjectList to fetch live projects even before step 1 is complete.
  const tempSessionId = useRef(`temp-wizard-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  // Credentials stored for re-save under real appId once draftAppId arrives
  const pendingTokenRef = useRef<{
    sonar?: { url: string; token: string; org: string };
    blackduck?: { url: string; token: string };
    trivy?: boolean;
    polaris?: string;
  }>({});
  const [trivyImages, setTrivyImages] = useState<string[]>([]);
  const [trivyMappings, setTrivyMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [trivyMappingsDialog, setTrivyMappingsDialog] = useState(false);
  // Tracks the Polaris applicationId that the current saved/validation state
  // reflects (persisted or last validated), so further edits reset that state.
  const confirmedPolarisIdRef = useRef<string>('');
  const [sonarProjectMappings, setSonarProjectMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [bdProjectMappings, setBdProjectMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [newMgmtEmail, setNewMgmtEmail] = useState('');
  const [newMgmtLevel, setNewMgmtLevel] = useState('');
  const [mgmtAccessTouched, setMgmtAccessTouched] = useState(false);

  const getRepoNameFromUrl = (url: string): string => {
    const match = url.match(/\/([^/]+)\/([^/]+?)(?:\.git)?$/);
    return match ? `${match[1]}/${match[2]}` : url;
  };

  const STEPS = [
    t('onboarding.steps.projectInfo'), t('onboarding.steps.repository'),
    t('onboarding.steps.connections'), t('onboarding.steps.membership'), t('onboarding.steps.reviewSubmit'),
  ];

  const setField = <K extends keyof OnboardingFormData>(key: K, value: OnboardingFormData[K]) => {
    if (key === 'ghePat') { setGhePatVerified(false); setConnValidation((prev) => ({ ...prev, ghe: null })); }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const configName = deriveConfigName(form);
  const generatedYaml = buildYaml(form);

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    downloadFile(
      buildYaml({ ...EMPTY_FORM, projectName: form.projectName || 'my-project', leanIxId: form.leanIxId || 'APP-', spocs: form.spocs, gheRepos: form.gheRepos }),
      'config-template.yaml',
    );
  };

  const processYamlFile = (file: File) => {
    if (!/\.ya?ml$/i.test(file.name)) { setSnackMsg(t('onboarding.form.invalidYamlFile')); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseYamlToForm(text);
      parsed.leanIxId = parsed.leanIxId ? normalizeAppId(parsed.leanIxId) : 'APP-';
      if (parsed.spocs.length === 0) parsed.spocs = [{ name: '', email: '' }];
      if (parsed.gheRepos.length === 0) parsed.gheRepos = [{ url: '', branch: '', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' }];
      setForm(parsed);
      setSnackMsg(t('onboarding.form.yamlImported'));
    };
    reader.readAsText(file);
  };

  const handleYamlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processYamlFile(file);
    e.target.value = '';
  };

  const [isDragging, setIsDragging] = useState(false);
  const yamlFileInputRef = useRef<HTMLInputElement>(null);
  const [reviewTab, setReviewTab] = useState(0);
  const [reviewRepoIdx, setReviewRepoIdx] = useState(0);
  const handleYamlDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processYamlFile(file);
  };

  useEffect(() => {
    if (mode !== 'edit' || !appId) return;
    (async () => {
      setLoadingExisting(true);
      try {
        const detail = await fetchOnboardingDetail(appId);
        const parsed = parseYamlToForm(detail.yaml_content);
        // If this project hasn't been through the secops wizard yet (discovered via search), the YAML
        // is empty — fall back to the project's existing name/APP ID so the user isn't starting blank.
        parsed.projectName = parsed.projectName || detail.appName || detail.config_name || '';
        const leanIxSource = parsed.leanIxId || detail.leanIXId || '';
        parsed.leanIxId = leanIxSource ? normalizeAppId(leanIxSource) : 'APP-';
        if (parsed.spocs.length === 0) parsed.spocs = [{ name: '', email: '' }];
        if (parsed.gheRepos.length === 0) parsed.gheRepos = [{ url: '', branch: '', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' }];
        // Members are stored separately in the projectUsers table, not in the YAML — load them from the response
        if (Array.isArray(detail.members) && detail.members.length > 0) {
          parsed.members = detail.members as MemberEntry[];
        }
        // Management access: prefer the embedded field returned by getSecopsOnboardingDetail
        // (added to the response to avoid a separate round-trip that could fail silently).
        // Fall back to the dedicated endpoint if the embedded field is absent (older backend versions).
        if (Array.isArray(detail.managementAccess)) {
          parsed.managementAccess = detail.managementAccess as ManagementAccessEntry[];
        } else {
          try {
            const mgmtEntries = await fetchManagementAccessEntries(appId);
            parsed.managementAccess = mgmtEntries as ManagementAccessEntry[];
          } catch { /* non-fatal — management access step will show empty, user can re-add */ }
        }
        setForm(parsed);

        // Clear YAML placeholder values for secrets (they come back as "${VAR_NAME}" strings)
        setForm((prev) => ({
          ...prev,
          ghePat: prev.ghePat.startsWith('${') ? '' : prev.ghePat,
          sonarToken: prev.sonarToken.startsWith('${') ? '' : prev.sonarToken,
          blackduckApiToken: prev.blackduckApiToken.startsWith('${') ? '' : prev.blackduckApiToken,
          trivyUsername: prev.trivyUsername.startsWith('${') ? '' : prev.trivyUsername,
          trivyRobotSecret: prev.trivyRobotSecret.startsWith('${') ? '' : prev.trivyRobotSecret,
        }));

        // Set draftAppId so InlineProjectList / InlineTrivyImageList have a project context
        setDraftAppId(appId);

        // Restore tool connection state from saved tokens
        try {
          const tokenRes = await sentinelNetwork.get(`/config/project/${appId}/tokens`);
          const tokens: any[] = tokenRes.data?.data || [];

          const sonarToken = tokens.find((t) => t.platform === 'SonarQube');
          if (sonarToken?.encryptedConfig) {
            setSonarTokenSaved(true);
            if (Array.isArray(sonarToken.mappings) && sonarToken.mappings.length > 0) {
              setSonarProjectMappings(sonarToken.mappings);
            }
          }

          const bdToken = tokens.find((t) => t.platform === 'BlackDuck');
          if (bdToken?.encryptedConfig) {
            setBdTokenSaved(true);
            if (Array.isArray(bdToken.mappings) && bdToken.mappings.length > 0) {
              setBdProjectMappings(bdToken.mappings);
            }
          }

          const harborToken = tokens.find((t) => t.platform === 'Harbor' || t.platform === 'Trivy');
          if (harborToken?.encryptedConfig) {
            setTrivyTokenSaved(true);
            if (Array.isArray(harborToken.images) && harborToken.images.length > 0) {
              setTrivyImages(harborToken.images);
            }
            if (Array.isArray(harborToken.mappings) && harborToken.mappings.length > 0) {
              setTrivyMappings(harborToken.mappings);
            }
            // Pre-fill registry fields from stored URL so the UI shows them
            if (harborToken.url && !parsed.containerRegistry) {
              setForm((prev) => ({ ...prev, containerRegistry: harborToken.url }));
            }
          }

          const polarisToken = tokens.find((t) => t.platform === 'Polaris');
          if (polarisToken?.applicationId) {
            setPolarisConfigSaved(true);
            confirmedPolarisIdRef.current = polarisToken.applicationId;
            if (!parsed.polarisApplicationId) {
              setForm((prev) => ({ ...prev, polarisApplicationId: polarisToken.applicationId }));
            }
          }
        } catch { /* non-fatal — don't block edit load */ }

      } catch {
        setSnackMsg(t('common.error'));
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [mode, appId]);

  useEffect(() => {
    (async () => {
      try {
        const { userEmail } = await fetchEntitlements();
        setCurrentUserEmail(userEmail);
      } catch { /* ignore */ }
    })();
  }, []);

  // When draftAppId becomes available, retry any token saves that failed earlier
  // because the project hadn't been created yet (projectName was empty at validation time).
  useEffect(() => {
    if (!draftAppId) return;
    const pending = pendingTokenRef.current;
    if (pending.sonar && !sonarTokenSaved) {
      const { url, token, org } = pending.sonar;
      delete pendingTokenRef.current.sonar;
      saveToolToken('SonarQube', url, token, org).then((id) => { if (id) setSonarTokenSaved(true); });
    }
    if (pending.blackduck && !bdTokenSaved) {
      const { url, token } = pending.blackduck;
      delete pendingTokenRef.current.blackduck;
      saveToolToken('BlackDuck', url, token).then((id) => { if (id) setBdTokenSaved(true); });
    }
    if (pending.trivy && !trivyTokenSaved) {
      delete pendingTokenRef.current.trivy;
      saveTrivyToken().then((id) => { if (id) setTrivyTokenSaved(true); });
    }
    if (pending.polaris && !polarisConfigSaved) {
      const applicationId = pending.polaris;
      delete pendingTokenRef.current.polaris;
      savePolarisConfigToBackend(applicationId).then((id) => { if (id) setPolarisConfigSaved(true); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftAppId]);

  // Reset Polaris saved/validation state when the user edits the application UUID.
  useEffect(() => {
    const current = form.polarisApplicationId.trim();
    if (current && current !== confirmedPolarisIdRef.current) {
      setPolarisConfigSaved(false);
      setConnValidation((prev) => ({ ...prev, polaris: null }));
    }
  }, [form.polarisApplicationId]);

  const updateRepo = (idx: number, field: keyof GheRepo, value: string) => {
    setForm((prev) => { const gheRepos = [...prev.gheRepos]; (gheRepos[idx] as any)[field] = value; return { ...prev, gheRepos }; });
  };
  const updateRepoTech = (idx: number, field: 'languages' | 'frameworks' | 'buildTools', value: string[]) => {
    setForm((prev) => { const gheRepos = [...prev.gheRepos]; (gheRepos[idx] as any)[field] = value; return { ...prev, gheRepos }; });
  };
  const addRepo = () => setForm((prev) => ({ ...prev, gheRepos: [...prev.gheRepos, { url: '', branch: '', languages: [], frameworks: [], buildTools: [], criticality: '', type: '' }] }));
  const removeRepo = (idx: number) => setForm((prev) => ({ ...prev, gheRepos: prev.gheRepos.filter((_, i) => i !== idx) }));

  const loadBranches = useCallback((repoUrl: string) => {
    const repoKey = getRepoNameFromUrl(repoUrl);
    if (fetchBranchDebounce.current[repoKey]) { clearTimeout(fetchBranchDebounce.current[repoKey]); delete fetchBranchDebounce.current[repoKey]; }
    if (!repoUrl) return;
    const urlPattern = /^https?:\/\/[^/]+\/[^/]+\/[^/]+?(\.git)?$/;
    if (!urlPattern.test(repoUrl)) { setSnackMsg(t('onboarding.form.invalidRepoUrl')); return; }
    if (!form.ghePat) { setSnackMsg(t('onboarding.form.gheTokenRequired')); return; }
    fetchBranchDebounce.current[repoKey] = setTimeout(async () => {
      setBranchLoading((prev) => ({ ...prev, [repoKey]: true }));
      try {
        const branches = await fetchGheBranches(form.ghePat, repoUrl);
        if (branches.length === 0) setSnackMsg(t('onboarding.form.noBranchesFetched'));
        setRepoBranches((prev) => ({ ...prev, [repoKey]: branches }));
      } catch {
        setSnackMsg(t('onboarding.form.unableToFetchBranchDetails'));
        setRepoBranches((prev) => ({ ...prev, [repoKey]: [] }));
      } finally {
        setBranchLoading((prev) => ({ ...prev, [repoKey]: false }));
        delete fetchBranchDebounce.current[repoKey];
      }
    }, 300);
  }, [form.ghePat, t]);

  const validateConn = async (tool: 'ghe' | 'sonar' | 'blackduck' | 'trivy' | 'polaris') => {
    setConnValidation((prev) => ({ ...prev, [tool]: { loading: true, result: null } }));
    let result: ValidationResult;
    try {
      if (tool === 'polaris') {
        const parsedId = parsePolarisApplicationId(form.polarisApplicationId);
        if (!parsedId) {
          result = { ok: false, message: t('onboarding.form.polarisInvalidInput') };
        } else {
          // Normalize a pasted URL to the bare UUID so the field reflects what was detected.
          if (parsedId !== form.polarisApplicationId.trim()) setField('polarisApplicationId', parsedId);
          confirmedPolarisIdRef.current = parsedId;
          result = await validatePolarisConnection(parsedId);
          if (result.ok) {
            const savedId = await savePolarisConfigToBackend(parsedId);
            setPolarisConfigSaved(!!savedId);
            if (savedId === tempSessionId.current) {
              pendingTokenRef.current.polaris = parsedId;
            }
          }
        }
      } else if (tool === 'ghe') {
        result = await validateGheConnection(form.ghePat, '');
        setGhePatVerified(result.ok);
        if (result.ok) form.gheRepos.forEach((r) => { if (r.url) loadBranches(r.url); });
      } else if (tool === 'sonar') {
        result = await validateSonarConnection(form.sonarUrl, form.sonarToken);
        if (result.ok) {
          // Save token to DynamoDB so InlineProjectList can fetch the live project list
          const savedId = await saveToolToken('SonarQube', form.sonarUrl, form.sonarToken, form.sonarOrg);
          setSonarTokenSaved(!!savedId);
          // Store for re-save under the real appId once the project is created in step 1
          if (savedId === tempSessionId.current) {
            pendingTokenRef.current.sonar = { url: form.sonarUrl, token: form.sonarToken, org: form.sonarOrg };
          }
        }
      } else if (tool === 'blackduck') {
        result = await validateBlackDuckConnection(form.blackduckUrl, form.blackduckApiToken);
        if (result.ok) {
          const savedId = await saveToolToken('BlackDuck', form.blackduckUrl, form.blackduckApiToken);
          setBdTokenSaved(!!savedId);
          if (savedId === tempSessionId.current) {
            pendingTokenRef.current.blackduck = { url: form.blackduckUrl, token: form.blackduckApiToken };
          }
        }
      } else {
        result = await validateTrivyConnection(form.containerRegistry, form.trivyUsername, form.trivyRobotSecret);
        // Save token even on validation failure — registry may be unreachable from cloud
        if (result.ok) {
          const savedId = await saveTrivyToken();
          setTrivyTokenSaved(!!savedId);
          if (savedId === tempSessionId.current) pendingTokenRef.current.trivy = true;
        }
        // If validation failed, we show "Save Anyway" — don't save here
      }
    } catch (e: any) {
      result = { ok: false, message: e?.message || 'Unexpected error' };
    }
    setConnValidation((prev) => ({ ...prev, [tool]: { loading: false, result } }));
  };

  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: {
        if (!form.projectName.trim()) return false;
        if (!isValidAppId(form.leanIxId)) return false;
        return true;
      }
      case 1: {
        // At least one repo with a URL must be added
        const reposWithUrl = form.gheRepos.filter((r) => r.url.trim());
        if (reposWithUrl.length === 0) return false;
        // Every repo that has a URL must also have a branch selected
        return !reposWithUrl.some((r) => !r.branch.trim());
      }
      case 3:
        return form.managementAccess.length > 0;
      default:
        return true;
    }
  }, [form]);

  /**
   * Fraction (0..1) of a step's required fields that are filled in and valid — used to color
   * the stepper: 0 = red (nothing done), 1 = green (fully complete), in between = amber (partial).
   * Steps without explicit required fields (Connections, Review) fall back to isStepValid.
   */
  const getStepFillRatio = useCallback((step: number): number => {
    switch (step) {
      case 0: {
        const checks = [
          !!form.projectName.trim(),
          isValidAppId(form.leanIxId),
        ];
        return checks.filter(Boolean).length / checks.length;
      }
      case 1: {
        const withUrl = form.gheRepos.filter((repo) => repo.url.trim());
        if (withUrl.length === 0) return 0;  // no repos at all → red
        const withBranch = withUrl.filter((repo) => repo.branch.trim());
        return withBranch.length / withUrl.length;
      }
      default:
        return isStepValid(step) ? 1 : 0;
    }
  }, [form, isStepValid]);

  const allStepsValid = STEPS.slice(0, STEPS.length - 1).every((_, i) => isStepValid(i));

  /**
   * Best-effort partial save so progress isn't lost if the user leaves mid-wizard.
   * Returns `false` (and sets `formError`) if the backend rejects the save due to a
   * duplicate application name/APP ID — callers must not advance the step in that case.
   */
  const saveDraft = async (): Promise<string | null> => {
    if (!form.projectName.trim() || !isValidAppId(form.leanIxId)) return draftAppId ?? null;
    setSavingDraft(true);
    try {
      const payload = { config_name: configName, yaml_content: generatedYaml, members: form.members };
      if (draftAppId) {
        await updateOnboarding(draftAppId, payload);
        return draftAppId;
      } else {
        const response = await createOnboarding(payload);
        const newId = response.data.appId;
        setDraftAppId(newId);
        return newId;
      }
    } catch (err: any) {
      // Non-blocking — surface failure but don't block navigation
      setSnackMsg(t('onboarding.form.draftSaveFailed'));
      return draftAppId ?? null;
    } finally {
      setSavingDraft(false);
    }
  };

  /**
   * Encrypt and persist a tool token to SENTINEL_PROJECT_TOKENS_TABLE so that
   * InlineProjectList can fetch the live project list from the tool's API.
   * Creates the draft project first if it doesn't exist yet.
   * Returns the appId used (draftAppId or newly created), or null on failure.
   */
  const saveToolToken = async (
    platform: 'SonarQube' | 'BlackDuck',
    url: string,
    token: string,
    org?: string,
  ): Promise<string | null> => {
    try {
      let currentDraftId: string | undefined = draftAppId;
      if (!currentDraftId) {
        const saved = await saveDraft();
        // Fall back to a temp session ID so InlineProjectList can still show projects.
        // The token will be re-saved under the real appId once the project is created.
        currentDraftId = saved || tempSessionId.current;
      }
      const { publicKey } = await fetchEntitlements();
      const base = extractBaseUrl(url);
      const configObj = { baseUrl: base, token, organization: org || '', platform, scope: 'shared' };
      const encryptedConfig = encryptString(JSON.stringify(configObj), publicKey);
      await sentinelNetwork.post(`/config/project/${currentDraftId}/tokens`, {
        platform,
        encryptedConfig,
        url: base,
        organization: org || undefined,
        scope: 'shared',
      });
      return currentDraftId;
    } catch (err) {
      console.error('[wizard] saveToolToken failed:', err);
      return null;
    }
  };

  /** Encrypt and save Harbor (Trivy) credentials. Returns appId on success, null on failure. */
  const saveTrivyToken = async (): Promise<string | null> => {
    try {
      let currentDraftId: string | undefined = draftAppId;
      if (!currentDraftId) {
        const saved = await saveDraft();
        currentDraftId = saved || tempSessionId.current;
      }
      const { publicKey } = await fetchEntitlements();
      const configObj = {
        registryUrl: form.containerRegistry.trim(),
        project: (form.containerProject || '').trim(),
        username: form.trivyUsername.trim(),
        robotSecret: form.trivyRobotSecret,
        platform: 'Harbor',
      };
      const encryptedConfig = encryptString(JSON.stringify(configObj), publicKey);
      await sentinelNetwork.post(`/config/project/${currentDraftId}/tokens`, {
        platform: 'Harbor',
        encryptedConfig,
        url: form.containerRegistry.trim(),
      });
      setTrivyTokenSaved(true);
      return currentDraftId;
    } catch (err) {
      console.error('[wizard] saveTrivyToken failed:', err);
      return null;
    }
  };

  /**
   * Save the project's Polaris application UUID via the dedicated (non-secret) Polaris
   * connection route. No credential is ever sent — Sentinel authenticates with one
   * centrally managed service-account token (see docs/POLARIS_ONBOARDING.md).
   * Returns the appId used (draftAppId or newly created), or null on failure.
   */
  const savePolarisConfigToBackend = async (applicationId: string): Promise<string | null> => {
    try {
      let currentDraftId: string | undefined = draftAppId;
      if (!currentDraftId) {
        const saved = await saveDraft();
        currentDraftId = saved || tempSessionId.current;
      }
      await sentinelNetwork.post(`/config/project/${currentDraftId}/polaris`, { applicationId });
      return currentDraftId;
    } catch (err) {
      console.error('[wizard] savePolarisConfigToBackend failed:', err);
      return null;
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      setTouched((p) => ({ ...p, projectName: true, leanIxId: true }));
    }
    if (activeStep === 3) {
      setMgmtAccessTouched(true);
      if (form.managementAccess.length === 0) return;
    }
    setFormError(null);
    await saveDraft(); // Partially persist progress to DB on every step advance
    setActiveStep((s) => s + 1);
  };
  const handleBack = () => { setFormError(null); setActiveStep((s) => s - 1); };
  const handleStepClick = (target: number) => { if (target !== activeStep) { setFormError(null); setActiveStep(target); } };

  /**
   * Whether the saved configuration is fully complete: repository/branch info filled for every
   * repo, and no filled-in tool has an *explicit* failed validation from this session. A tool
   * that hasn't been re-validated in the current session (e.g. resubmitting an already-configured
   * project without clicking "Validate Connection" again) is treated as fine, not as a failure —
   * only a real `ok: false` result counts against completeness.
   * Used to decide between the "success" and "partial" completion messages.
   */
  const isConfigurationComplete = (): boolean => {
    if (getStepFillRatio(2) < 1) return false;
    const toolChecks: Array<['ghe' | 'sonar' | 'blackduck' | 'trivy' | 'polaris', boolean]> = [
      ['ghe', form.gheRepos.some((r) => r.url.trim()) && !!form.ghePat],
      ['sonar', !!(form.sonarUrl && form.sonarToken)],
      ['blackduck', !!(form.blackduckUrl && form.blackduckApiToken)],
      ['trivy', !!(form.containerRegistry && form.trivyUsername && form.trivyRobotSecret)],
      ['polaris', !!form.polarisApplicationId.trim() && polarisConfigSaved],
    ];
    return toolChecks.every(([tool, filled]) => !filled || connValidation[tool]?.result?.ok !== false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = { config_name: configName, yaml_content: generatedYaml, members: form.members };
      const targetAppId = draftAppId || (mode === 'edit' ? appId : undefined);
      let savedAppId: string | undefined = targetAppId;
      if (targetAppId) {
        await updateOnboarding(targetAppId, payload);
      } else {
        const response = await createOnboarding(payload);
        savedAppId = response.data.appId;
      }
      // Save management access entries — collect failures and warn the user (don't block submit)
      if (savedAppId && form.managementAccess.length > 0) {
        const mgmtResults = await Promise.allSettled(
          form.managementAccess.map((entry) => saveManagementAccessEntry(savedAppId!, entry))
        );
        const mgmtFailed = mgmtResults.filter((r) => r.status === 'rejected');
        if (mgmtFailed.length > 0) {
          const firstErr = (mgmtFailed[0] as PromiseRejectedResult).reason;
          const errMsg = firstErr?.response?.data?.message || firstErr?.message || 'Unknown error';
          console.error('[handleSubmit] Management access save failed:', errMsg, mgmtFailed);
          setSnackMsg(`Management access could not be saved: ${errMsg}`);
        }
      }
      // Tool KPI scans (Sonar/BlackDuck/GHE/Trivy + scoring) are triggered server-side
      // by the onboarding handler. Fire a frontend backup trigger so the pipeline starts
      // even if the backend trigger silently failed (e.g. SFN not yet configured in ENV).
      if (savedAppId) {
        triggerScanPipeline(savedAppId, 'onboarding').catch((e) => console.warn('[OnboardingWizard] scan trigger failed:', e?.message));
        // Kick off Delta AI KPI Batch scan for all configured git repos
        const aiRepos = form.gheRepos
          .filter((r) => r.url?.trim())
          .map((r) => {
            const match = r.url.match(/\/([^/]+?)(?:\.git)?$/);
            return { repoSlug: match ? match[1] : r.url, url: r.url, branch: r.branch || 'main' };
          });
        if (aiRepos.length > 0) {
          triggerAiScan(savedAppId, aiRepos).catch((e) => console.warn('[OnboardingWizard] Delta AI KPI scan trigger failed:', e?.message));
        }
      }
      navigate('/projects');
    } catch (err: any) {
      setFormError(err?.response?.data?.message || err?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NerdSpinner />
      </Box>
    );
  }

  if (submitSuccess) {
    const isPartial = submitSuccess.status === 'partial';
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 4 }}>
        <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: isPartial ? 'warning.light' : 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          {isPartial
            ? <ErrorOutlineIcon sx={{ fontSize: 48, color: 'warning.dark' }} />
            : <CheckCircleIcon sx={{ fontSize: 48, color: 'success.dark' }} />}
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {isPartial ? t('onboarding.form.submitPartialTitle') : t('onboarding.form.submitSuccessTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {isPartial
            ? t('onboarding.form.submitPartialMessage', { name: submitSuccess.name })
            : t('onboarding.form.submitSuccessMessage', { name: submitSuccess.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your security scan is running in the background. Navigate to the{' '}
          <Typography
            component="span"
            variant="body2"
            sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate('/projects')}
          >
            Projects page
          </Typography>{' '}
          to view security insights and KPIs.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/projects')}
          sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
        >
          Go to Projects
        </Button>
      </Box>
    );
  }

  const renderProjectInfo = () => (
    <Box>
      <Box
        onClick={() => yamlFileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleYamlDrop}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 2.5, borderRadius: 1.5,
          border: '1.5px dashed', borderColor: isDragging ? 'primary.main' : 'divider',
          bgcolor: isDragging ? 'action.hover' : 'transparent',
          cursor: 'pointer', transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        <CloudUploadIcon fontSize="small" color={isDragging ? 'primary' : 'action'} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>{t('onboarding.form.uploadYaml')}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{t('onboarding.form.dropzoneHint')}</Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(); }}
          sx={{ textTransform: 'none', fontSize: 12.5, flexShrink: 0 }}
        >
          {t('onboarding.form.downloadTemplate')}
        </Button>
        <input ref={yamlFileInputRef} type="file" accept=".yaml,.yml" hidden onChange={handleYamlUpload} />
      </Box>
      <FieldRow>
        <TextField label={t('onboarding.form.projectName')} value={form.projectName} onChange={(e) => setField('projectName', e.target.value)} onBlur={() => markTouched('projectName')}
          required size="small" error={isTouched('projectName') && !form.projectName.trim()}
          helperText={isTouched('projectName') && !form.projectName.trim() ? t('onboarding.form.projectNameRequired') : ''}
          sx={errSx(isTouched('projectName') && !form.projectName.trim())} />
        <TextField label={t('onboarding.form.leanIxId')} value={form.leanIxId} onChange={(e) => setField('leanIxId', normalizeAppId(e.target.value))} onBlur={() => markTouched('leanIxId')}
          required size="small" placeholder="APP-12345" error={isTouched('leanIxId') && !isValidAppId(form.leanIxId)}
          helperText={isTouched('leanIxId') && !isValidAppId(form.leanIxId) ? t('onboarding.form.leanIxIdInvalid') : ''}
          sx={errSx(isTouched('leanIxId') && !isValidAppId(form.leanIxId))} />
      </FieldRow>
      <FieldRow>
        <TextField label={t('onboarding.form.projectPhase')} value={form.projectPhase} onChange={(e) => setField('projectPhase', e.target.value)} select size="small">
          {PROJECT_PHASES.map((p) => <MenuItem key={p} value={p}>{p.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
        <Box />
      </FieldRow>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>{t('onboarding.form.classification')}</Typography>
      <FieldRow>
        <TextField label={t('onboarding.form.criticality')} value={form.criticality} onChange={(e) => setField('criticality', e.target.value)} select size="small">
          {CRITICALITY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label={t('onboarding.form.dataClassification')} value={form.dataClassification} onChange={(e) => setField('dataClassification', e.target.value)} select size="small">
          {DATA_CLASSIFICATION_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </FieldRow>
      <Autocomplete multiple freeSolo size="small" options={COMPLIANCE_OPTIONS} value={form.complianceTags} onChange={(_, v) => setField('complianceTags', v)}
        renderInput={(params) => <TextField {...params} label={t('onboarding.form.complianceTags')} />} />
    </Box>
  );

  const renderValidationChip = (tool: 'ghe' | 'sonar' | 'blackduck' | 'trivy' | 'polaris') => {
    const v = connValidation[tool];
    if (!v) return null;
    if (v.loading) return <Chip icon={<NerdSpinner size={14} />} label="Validating…" size="small" variant="outlined" />;
    if (v.result?.ok) return <Chip icon={<CheckCircleOutlineIcon fontSize="small" />} label={v.result.message} size="small" color="success" variant="outlined" />;
    return <Chip icon={<ErrorOutlineIcon fontSize="small" />} label={v.result?.message || 'Failed'} size="small" color="error" variant="outlined" />;
  };

  const renderRepository = () => (
    <Box>
      {/* GHE Token — verify first before adding repos */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
          GHE Authentication
        </Typography>
        <TextField
          label={t('onboarding.form.ghePat')}
          value={form.ghePat}
          onChange={(e) => setField('ghePat', e.target.value)}
          size="small"
          type="password"
          placeholder="ghp_..."
          fullWidth
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={connValidation.ghe?.loading ? <NerdSpinner size={16} /> : <WifiTetheringIcon />}
            disabled={!form.ghePat || !!connValidation.ghe?.loading}
            onClick={() => validateConn('ghe')}
            sx={{ textTransform: 'none' }}
          >
            Verify & Save
          </Button>
          {renderValidationChip('ghe')}
        </Box>
        <Alert severity="info" variant="outlined" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          Your token is encrypted end-to-end and stored securely. It is never exposed in logs or to other users.
        </Alert>
      </Paper>

      {/* Repositories — only enabled after PAT is verified */}
      <Box sx={{ opacity: ghePatVerified ? 1 : 0.5, pointerEvents: ghePatVerified ? 'auto' : 'none' }}>
        {!ghePatVerified && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Verify your GHE Personal Access Token above before adding repositories.
          </Alert>
        )}
        {form.gheRepos.map((repo, idx) => {
          const repoKey = getRepoNameFromUrl(repo.url);
          const branches = repoBranches[repoKey] || [];
          const isLoadingBranches = branchLoading[repoKey];
          return (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Repository {idx + 1}
                </Typography>
                {form.gheRepos.length > 1 && (
                  <IconButton size="small" color="error" onClick={() => removeRepo(idx)}><DeleteIcon fontSize="small" /></IconButton>
                )}
              </Box>
              <TextField
                label={t('onboarding.form.gheRepoUrl')}
                value={repo.url}
                onChange={(e) => updateRepo(idx, 'url', e.target.value)}
                onBlur={() => loadBranches(repo.url)}
                size="small"
                fullWidth
                placeholder="https://github.enterprise.com/org/repo"
                sx={{ mb: 1.5 }}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={branches}
                value={repo.branch}
                loading={isLoadingBranches}
                onInputChange={(_, v, reason) => { if (reason === 'input') updateRepo(idx, 'branch', v); }}
                onChange={(_, v) => updateRepo(idx, 'branch', v || '')}
                onOpen={() => { if (branches.length === 0 && repo.url) loadBranches(repo.url); }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('onboarding.form.gheBranch')}
                    placeholder="Search or type a branch name"
                    helperText={isLoadingBranches ? t('onboarding.form.loadingBranches') : ''}
                  />
                )}
                sx={{ mb: 1.5, maxWidth: 360 }}
              />
              <Autocomplete multiple freeSolo size="small" options={LANGUAGE_OPTIONS} value={repo.languages || []} onChange={(_, v) => updateRepoTech(idx, 'languages', v)}
                renderInput={(params) => <TextField {...params} label={t('onboarding.form.languages')} />} sx={{ mb: 1.5 }} />
              <Autocomplete multiple freeSolo size="small" options={FRAMEWORK_OPTIONS} value={repo.frameworks || []} onChange={(_, v) => updateRepoTech(idx, 'frameworks', v)}
                renderInput={(params) => <TextField {...params} label={t('onboarding.form.frameworks')} />} sx={{ mb: 1.5 }} />
              <Autocomplete multiple freeSolo size="small" options={BUILD_TOOL_OPTIONS} value={repo.buildTools || []} onChange={(_, v) => updateRepoTech(idx, 'buildTools', v)}
                renderInput={(params) => <TextField {...params} label={t('onboarding.form.buildTools')} />} sx={{ mb: 1.5 }} />
              <FieldRow>
                <TextField label={t('onboarding.form.repoCriticality')} value={repo.criticality} onChange={(e) => updateRepo(idx, 'criticality', e.target.value)} select size="small">
                  {REPO_CRITICALITY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{t(`onboarding.form.repoCriticality${c.charAt(0)}${c.slice(1).toLowerCase()}`)}</MenuItem>)}
                </TextField>
                <TextField label={t('onboarding.form.repoType')} value={repo.type} onChange={(e) => updateRepo(idx, 'type', e.target.value)} select size="small">
                  {REPO_TYPE_OPTIONS.map((ty) => <MenuItem key={ty} value={ty}>{t(ty === 'INFRA' ? 'onboarding.form.repoTypeInfra' : 'onboarding.form.repoTypeNonInfra')}</MenuItem>)}
                </TextField>
              </FieldRow>
            </Paper>
          );
        })}
        <Button size="small" startIcon={<AddIcon />} onClick={addRepo}>{t('onboarding.form.addRepo')}</Button>
      </Box>
    </Box>
  );


  const renderConnections = () => (
    <Box>
      <ToolGroup icon={<DeviceHubIcon fontSize="small" color="primary" />} title={t('onboarding.form.sonarGroup')} connected={sonarTokenSaved}>
        <FieldRow>
          <TextField label={t('onboarding.form.sonarUrl')} value={form.sonarUrl} onChange={(e) => setField('sonarUrl', e.target.value)} size="small" placeholder="https://sonarcloud.io or self-hosted URL" />
          <TextField label={t('onboarding.form.sonarOrg')} value={form.sonarOrg} onChange={(e) => setField('sonarOrg', e.target.value)} size="small" />
        </FieldRow>
        <TextField label={t('onboarding.form.sonarToken')} value={form.sonarToken} onChange={(e) => setField('sonarToken', e.target.value)} size="small" type="password" fullWidth sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button size="small" variant="outlined" startIcon={<WifiTetheringIcon />} disabled={!form.sonarToken || !form.sonarUrl || !!connValidation.sonar?.loading} onClick={() => validateConn('sonar')} sx={{ textTransform: 'none' }}>
            {t('onboarding.form.validateConnection')}
          </Button>
          {renderValidationChip('sonar')}
        </Box>
        {/* Inline project picker — shown after token is validated & saved (real or temp appId) */}
        {sonarTokenSaved && (
          <>
            {sonarProjectMappings.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: 12 }}>
                Select at least one project below to enable <strong>Fetch Report</strong>.
              </Alert>
            )}
            <InlineProjectList
              key={draftAppId || tempSessionId.current}
              appId={draftAppId || tempSessionId.current}
              platform="SonarQube"
              savedMappings={sonarProjectMappings}
              gitRepos={form.gheRepos
                .filter(r => r.url?.trim())
                .map(r => ({
                  name: r.url.replace('.git', '').split('/').pop() || '',
                  branch: r.branch || 'main',
                }))}
              onSave={async (mappings) => {
                setSonarProjectMappings(mappings);
                try {
                  const tokenRes = await sentinelNetwork.get(`/config/project/${draftAppId}/tokens`);
                  const token = (tokenRes.data?.data || []).find((t: any) => t.platform === 'SonarQube');
                  if (token?.encryptedConfig) {
                    await sentinelNetwork.post(`/config/project/${draftAppId}/tokens`, {
                      platform: 'SonarQube', encryptedConfig: token.encryptedConfig,
                      url: token.url, organization: token.organization, mappings, scope: token.scope,
                    });
                  }
                } catch { /* non-critical */ }
              }}
            />
          </>
        )}
      </ToolGroup>

      <ToolGroup icon={<DeviceHubIcon fontSize="small" color="primary" />} title={t('onboarding.form.blackduckGroup')} connected={bdTokenSaved}>
        <TextField label={t('onboarding.form.blackduckUrl')} value={form.blackduckUrl} onChange={(e) => setField('blackduckUrl', e.target.value)} size="small" fullWidth placeholder="https://your-org.app.blackduck.com" sx={{ mb: 1.5 }} />
        <TextField label={t('onboarding.form.blackduckToken')} value={form.blackduckApiToken} onChange={(e) => setField('blackduckApiToken', e.target.value)} size="small" type="password" fullWidth sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button size="small" variant="outlined" startIcon={<WifiTetheringIcon />} disabled={!form.blackduckApiToken || !form.blackduckUrl || !!connValidation.blackduck?.loading} onClick={() => validateConn('blackduck')} sx={{ textTransform: 'none' }}>
            {t('onboarding.form.validateConnection')}
          </Button>
          {renderValidationChip('blackduck')}
        </Box>
        {/* Inline project picker — shown after token is validated & saved (real or temp appId) */}
        {bdTokenSaved && (
          <>
            {bdProjectMappings.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: 12 }}>
                Select at least one project below to enable <strong>Fetch Report</strong>.
              </Alert>
            )}
            <InlineProjectList
              key={draftAppId || tempSessionId.current}
              appId={draftAppId || tempSessionId.current}
              platform="BlackDuck"
              savedMappings={bdProjectMappings}
              gitRepos={form.gheRepos
                .filter(r => r.url?.trim())
                .map(r => ({
                  name: r.url.replace('.git', '').split('/').pop() || '',
                  branch: r.branch || 'main',
                }))}
              onSave={async (mappings) => {
                setBdProjectMappings(mappings);
                try {
                  const tokenRes = await sentinelNetwork.get(`/config/project/${draftAppId}/tokens`);
                  const token = (tokenRes.data?.data || []).find((t: any) => t.platform === 'BlackDuck');
                  if (token?.encryptedConfig) {
                    await sentinelNetwork.post(`/config/project/${draftAppId}/tokens`, {
                      platform: 'BlackDuck', encryptedConfig: token.encryptedConfig,
                      url: token.url, mappings, scope: token.scope,
                    });
                  }
                } catch { /* non-critical */ }
              }}
            />
          </>
        )}
      </ToolGroup>

      <ToolGroup icon={<DeviceHubIcon fontSize="small" color="primary" />} title={t('onboarding.form.trivyGroup')} connected={trivyTokenSaved}>
        <TextField label={t('onboarding.form.containerRegistry')} value={form.containerRegistry} onChange={(e) => { setField('containerRegistry', e.target.value); setConnValidation((p) => ({ ...p, trivy: null })); }} size="small" fullWidth placeholder="registry-emea.app.corpintra.net" sx={{ mb: 1.5 }} />
        <TextField label={t('onboarding.form.containerProject')} value={form.containerProject} onChange={(e) => setField('containerProject', e.target.value)} size="small" fullWidth placeholder="nerd" sx={{ mb: 1.5 }} />
        <FieldRow>
          <TextField label={t('onboarding.form.trivyUsername')} value={form.trivyUsername} onChange={(e) => { setField('trivyUsername', e.target.value); setConnValidation((p) => ({ ...p, trivy: null })); }} size="small" />
          <TextField label={t('onboarding.form.trivySecret')} value={form.trivyRobotSecret} onChange={(e) => { setField('trivyRobotSecret', e.target.value); setConnValidation((p) => ({ ...p, trivy: null })); }} size="small" type="password" />
        </FieldRow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<WifiTetheringIcon />}
            disabled={!form.containerRegistry || !form.trivyUsername || !form.trivyRobotSecret || !!connValidation.trivy?.loading}
            onClick={() => validateConn('trivy')} sx={{ textTransform: 'none' }}>
            {connValidation.trivy?.loading ? 'Validating…' : t('onboarding.form.validateConnection')}
          </Button>

          {/* Save Anyway — shown when validation fails (registry unreachable from cloud) */}
          {connValidation.trivy?.result && !connValidation.trivy.result.ok && (
            <Button size="small" variant="contained" color="warning"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={async () => {
                const saved = await saveTrivyToken();
                if (saved) setConnValidation((p) => ({ ...p, trivy: { loading: false, result: { ok: true, message: 'Saved (validation skipped)' } } }));
              }}
              disabled={!form.containerRegistry || !form.trivyUsername || !form.trivyRobotSecret}
              sx={{ textTransform: 'none' }}>
              Save Anyway
            </Button>
          )}

          {renderValidationChip('trivy')}
        </Box>

        {/* Image picker — shown after token is saved (real or temp appId) */}
        {trivyTokenSaved && (
          <Box sx={{ mt: 2 }}>
            {trivyImages.length === 0 && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: 12 }}>
                Select at least one image below to enable <strong>Fetch Report</strong>.
              </Alert>
            )}
            <InlineTrivyImageList
              key={draftAppId || tempSessionId.current}
              appId={draftAppId || tempSessionId.current}
              savedImages={trivyImages}
              onSave={async (images) => {
                setTrivyImages(images);
                try {
                  const tokenRes = await sentinelNetwork.get(`/config/project/${draftAppId}/tokens`);
                  const token = (tokenRes.data?.data || []).find((t: any) => t.platform === 'Harbor');
                  if (token?.encryptedConfig) {
                    await sentinelNetwork.post(`/config/project/${draftAppId}/tokens`, {
                      platform: 'Harbor', encryptedConfig: token.encryptedConfig,
                      url: token.url, images, mappings: trivyMappings,
                    });
                  }
                } catch { /* non-critical */ }
              }}
            />

            {/* Image key mappings — for when Harbor image name differs from git repo name */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
              <Stack>
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: 12 }}>Image Mappings (optional)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Map each Git repo to its Harbor image path when the names differ.
                  {trivyMappings.length > 0 && ` ${trivyMappings.length} mapping${trivyMappings.length !== 1 ? 's' : ''} set.`}
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontSize: 11 }}
                onClick={() => setTrivyMappingsDialog(true)}>
                Edit Mappings
              </Button>
            </Stack>
          </Box>
        )}

        {/* Trivy image mappings dialog */}
        <EditMappingsDialog
          open={trivyMappingsDialog}
          platform="Harbor"
          mappings={trivyMappings}
          dataSources={form.gheRepos.filter(r => r.url).map(r => ({ platform: 'Git', url: r.url, id: r.url }))}
          onClose={() => setTrivyMappingsDialog(false)}
          onMappingsChange={setTrivyMappings}
          onSave={async () => {
            setTrivyMappingsDialog(false);
            try {
              const tokenRes = await sentinelNetwork.get(`/config/project/${draftAppId}/tokens`);
              const token = (tokenRes.data?.data || []).find((t: any) => t.platform === 'Harbor');
              if (token?.encryptedConfig) {
                await sentinelNetwork.post(`/config/project/${draftAppId}/tokens`, {
                  platform: 'Harbor', encryptedConfig: token.encryptedConfig,
                  url: token.url, images: trivyImages, mappings: trivyMappings,
                });
              }
            } catch { /* non-critical */ }
          }}
        />
      </ToolGroup>

      <ToolGroup icon={<DeviceHubIcon fontSize="small" color="primary" />} title={t('onboarding.form.polarisGroup')} connected={polarisConfigSaved}>
        <PolarisAppIdInstructions />
        <TextField
          label={t('onboarding.form.polarisApplicationId')}
          value={form.polarisApplicationId}
          onChange={(e) => { setField('polarisApplicationId', e.target.value); setConnValidation((p) => ({ ...p, polaris: null })); }}
          size="small"
          fullWidth
          placeholder="e.g. 703f7656-847d-40cb-a082-e55988369a31"
          helperText={t('onboarding.form.polarisApplicationIdHelp')}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={connValidation.polaris?.loading ? <NerdSpinner size={16} /> : <WifiTetheringIcon />}
            disabled={!form.polarisApplicationId.trim() || !!connValidation.polaris?.loading}
            onClick={() => validateConn('polaris')}
            sx={{ textTransform: 'none' }}
          >
            {t('onboarding.form.validateConnection')}
          </Button>
          {renderValidationChip('polaris')}
        </Box>
      </ToolGroup>
    </Box>
  );

  const renderMembership = () => {
    // Derive default admins from form data
    const adminEmails: string[] = [];
    if (currentUserEmail) adminEmails.push(currentUserEmail);
    const uniqueAdmins = [...new Set(adminEmails.map((e) => e.toLowerCase()))];

    const handleAddMember = () => {
      const email = newMemberEmail.trim().toLowerCase();
      if (!email || !isValidMBEmail(email)) return;
      if (uniqueAdmins.includes(email)) { setSnackMsg('This user is already an admin'); return; }
      if (form.members.some((m) => m.email.toLowerCase() === email)) { setSnackMsg('Member already added'); return; }
      setForm((prev) => ({ ...prev, members: [...prev.members, { email, role: 'member' }] }));
      setNewMemberEmail('');
    };

    const handleRemoveMember = (idx: number) => {
      setForm((prev) => ({ ...prev, members: prev.members.filter((_, i) => i !== idx) }));
    };

    const handleAddMgmtAccess = () => {
      const email = newMgmtEmail.trim().toLowerCase();
      if (!email || !isValidMBEmail(email)) { setSnackMsg('Please enter a valid @mercedes-benz.com email'); return; }
      if (!newMgmtLevel) return;
      if (form.managementAccess.some((e) => e.email.toLowerCase() === email)) { setSnackMsg('This email already has management access'); return; }
      setForm((prev) => ({ ...prev, managementAccess: [...prev.managementAccess, { email, managementLevel: newMgmtLevel }] }));
      setNewMgmtEmail('');
      setNewMgmtLevel('');
    };

    const handleRemoveMgmtAccess = (idx: number) => {
      setForm((prev) => ({ ...prev, managementAccess: prev.managementAccess.filter((_, i) => i !== idx) }));
    };

    return (
      <Box>
        {/* Default Admins (read-only) */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('onboarding.form.defaultAdmins')}</Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          {uniqueAdmins.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No admins derived yet — fill in project info first.</Typography>
          ) : (
            uniqueAdmins.map((email) => (
              <Box key={email} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: 13 }}>{email}</Typography>
                <Chip size="small" label="Admin" color="primary" variant="outlined" />
              </Box>
            ))
          )}
        </Paper>

        {/* Add members */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('onboarding.form.addMembers')}</Typography>
          <Tooltip title="Members must raise a SENTINEL_USERS role in order to access the project." arrow>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'default' }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label={t('onboarding.form.memberEmail')}
            value={newMemberEmail}
            onChange={(e) => setNewMemberEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMember(); } }}
            size="small"
            type="email"
            placeholder="user@mercedes-benz.com"
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMember} disabled={!newMemberEmail.trim()} sx={{ textTransform: 'none' }}>
            {t('onboarding.form.addMember')}
          </Button>
        </Box>

        {/* Members list */}
        {form.members.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
            {form.members.map((member, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: 13 }}>{member.email}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label="Member" color="default" variant="outlined" />
                  <Button size="small" color="error" onClick={() => handleRemoveMember(idx)} sx={{ minWidth: 'auto', p: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        {/* Management Access (mandatory) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mt: 1 }}>
          <SecurityIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Management Access <Typography component="span" color="error.main" sx={{ fontWeight: 700 }}>*</Typography>
          </Typography>
          <Tooltip title="Management must raise a SENTINEL_USERS role in order to access the project." arrow>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'default' }} />
          </Tooltip>
        </Box>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: 12.5 }}>
          Manage escalation levels for this project. At least one entry is required.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Email"
            value={newMgmtEmail}
            onChange={(e) => setNewMgmtEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMgmtAccess(); } }}
            size="small"
            type="email"
            placeholder="user@mercedes-benz.com"
            sx={{ flex: 2, minWidth: 180 }}
            required
          />
          <FormControl size="small" sx={{ flex: 1.5, minWidth: 160 }} required>
            <InputLabel>Management Level</InputLabel>
            <Select
              value={newMgmtLevel}
              label="Management Level"
              onChange={(e) => setNewMgmtLevel(e.target.value)}
            >
              {ONBOARDING_MANAGEMENT_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddMgmtAccess}
            disabled={!newMgmtEmail.trim() || !newMgmtLevel}
            sx={{ textTransform: 'none', minWidth: 90 }}
          >
            Add
          </Button>
        </Box>
        {mgmtAccessTouched && form.managementAccess.length === 0 && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: 1 }}>
            At least one management access entry is required.
          </Alert>
        )}
        {form.managementAccess.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            {form.managementAccess.map((entry, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ fontSize: 13 }}>{entry.email}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={entry.managementLevel} color="primary" variant="outlined" />
                  <Button size="small" color="error" onClick={() => handleRemoveMgmtAccess(idx)} sx={{ minWidth: 'auto', p: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    );
  };

  const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary', textAlign: 'right' }}>{value}</Typography>
    </Box>
  );

  /**
   * Shows the actual connection state on the review step: grey when the fields are empty,
   * red when the fields are filled but the last validation attempt failed (`ok: false`),
   * and green only when filled and either unvalidated or explicitly validated successfully.
   */
  const ConfiguredChip: React.FC<{ configured: boolean; validation?: ConnStatus }> = ({ configured, validation }) => {
    const failed = configured && !!validation && !validation.loading && validation.result?.ok === false;
    if (failed) {
      return (
        <Chip size="small" variant="outlined" color="error" icon={<ErrorOutlineIcon fontSize="small" />}
          label={t('onboarding.form.connectionFailed')} />
      );
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        color={configured ? 'success' : 'default'}
        icon={configured ? <CheckCircleOutlineIcon fontSize="small" /> : undefined}
        label={configured ? t('onboarding.form.configured') : t('onboarding.form.notConfigured')}
      />
    );
  };

  const renderReview = () => {
    const configuredRepos = form.gheRepos.filter((r) => r.url);
    const activeRepoIdx = form.gheRepos.indexOf(configuredRepos[reviewRepoIdx] || configuredRepos[0]);
    const pipelineTabs = [
      { label: t('onboarding.form.generatedYaml'), content: generatedYaml, filename: `${configName}.yaml` },
      ...(activeRepoIdx >= 0 ? [
        { label: t('onboarding.form.tabCi'), content: generateCiYaml(form, activeRepoIdx), filename: `${configName}_ci.yaml` },
        { label: t('onboarding.form.tabCicd'), content: generateCicdYaml(form, activeRepoIdx), filename: `${configName}_cicd.yaml` },
        { label: t('onboarding.form.tabPr'), content: generatePrYaml(form, activeRepoIdx), filename: `${configName}_pr.yaml` },
      ] : []),
    ];
    const currentTab = pipelineTabs[reviewTab] || pipelineTabs[0];

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
          {form.projectName.trim()
            ? t('onboarding.form.reviewIntro', { name: form.projectName })
            : t('onboarding.form.reviewIntroEmpty')}
        </Alert>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <SummaryRow label={t('onboarding.form.projectName')} value={form.projectName || '—'} />
          <SummaryRow label={t('onboarding.form.leanIxId')} value={form.leanIxId || '—'} />
          <SummaryRow label={t('onboarding.form.projectPhase')} value={form.projectPhase.replace(/_/g, ' ')} />
          <SummaryRow label={t('onboarding.form.criticality')} value={form.criticality || '—'} />
          <SummaryRow label={t('onboarding.form.dataClassification')} value={(form.dataClassification || '—').replace(/_/g, ' ')} />
          {(!form.projectName.trim() || !isValidAppId(form.leanIxId)) && (
            <Alert severity="warning" sx={{ mt: 1, py: 0.25, fontSize: 12.5 }}>
              Project name and a valid LeanIX ID are required — go back to step 1.
            </Alert>
          )}
        </Paper>

        {configuredRepos.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2, py: 0.25, fontSize: 12.5 }}>
            No GHE repositories configured — at least one repository is required.
          </Alert>
        )}
        {configuredRepos.map((repo, idx) => {
          const repoName = getRepoNameFromUrl(repo.url) || repo.url;
          return (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
              <SummaryRow label={t('onboarding.form.gheRepoUrl')} value={repoName} />
              <SummaryRow label={t('onboarding.form.gheBranch')} value={repo.branch || '—'} />
              <SummaryRow label={t('onboarding.form.languages')} value={(repo.languages?.length ? repo.languages : form.languages).join(', ') || '—'} />
              <SummaryRow label={t('onboarding.form.repoCriticality')} value={repo.criticality ? t(`onboarding.form.repoCriticality${repo.criticality.charAt(0)}${repo.criticality.slice(1).toLowerCase()}`) : '—'} />
              <SummaryRow label={t('onboarding.form.repoType')} value={repo.type ? t(repo.type === 'INFRA' ? 'onboarding.form.repoTypeInfra' : 'onboarding.form.repoTypeNonInfra') : '—'} />
            </Paper>
          );
        })}

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('onboarding.form.sonarGroup')}</Typography>
            <ConfiguredChip configured={!!(form.sonarUrl && form.sonarToken)} validation={connValidation.sonar} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('onboarding.form.blackduckGroup')}</Typography>
            <ConfiguredChip configured={!!(form.blackduckUrl && form.blackduckApiToken)} validation={connValidation.blackduck} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('onboarding.form.trivyGroup')}</Typography>
            <ConfiguredChip configured={!!(form.containerRegistry && form.trivyUsername && form.trivyRobotSecret)} validation={connValidation.trivy} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('onboarding.form.polarisGroup')}</Typography>
            <ConfiguredChip configured={!!form.polarisApplicationId.trim()} validation={connValidation.polaris} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          {form.members.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No members added</Typography>
          ) : (
            form.members.map((m, idx) => (
              <SummaryRow key={idx} label={m.email} value={m.role} />
            ))
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <SecurityIcon fontSize="small" color="primary" />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Management Access</Typography>
          </Box>
          {form.managementAccess.length === 0 ? (
            <Alert severity="warning" sx={{ py: 0.25, fontSize: 12.5 }}>No management access entries added — at least one is required.</Alert>
          ) : (
            form.managementAccess.map((e, idx) => (
              <SummaryRow key={idx} label={e.email} value={e.managementLevel} />
            ))
          )}
        </Paper>

        {configuredRepos.length > 1 && (
          <TextField
            select size="small" label={t('onboarding.form.repositoryStep')} value={reviewRepoIdx}
            onChange={(e) => setReviewRepoIdx(Number(e.target.value))}
            sx={{ mb: 1.5, minWidth: 240 }}
          >
            {configuredRepos.map((r, i) => (
              <MenuItem key={i} value={i}>{getRepoNameFromUrl(r.url) || r.url}</MenuItem>
            ))}
          </TextField>
        )}
        <Tabs value={reviewTab} onChange={(_, v) => setReviewTab(v)} sx={{ mb: 0, minHeight: 36 }}>
          {pipelineTabs.map((tab, i) => (
            <Tab key={i} label={tab.label} sx={{ minHeight: 36, py: 0.5, fontSize: 12.5, textTransform: 'none' }} />
          ))}
        </Tabs>
        <Paper variant="outlined" sx={{ borderRadius: 1, borderTopLeftRadius: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>{currentTab.filename}</Typography>
            <Button size="small" startIcon={<DownloadIcon fontSize="small" />} sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => downloadFile(currentTab.content, currentTab.filename)}>
              {t('onboarding.form.download')}
            </Button>
          </Box>
          <Box sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>{currentTab.content}</pre>
          </Box>
        </Paper>
      </Box>
    );
  };

  const stepContent = [renderProjectInfo, renderRepository, renderConnections, renderMembership, renderReview];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper variant="outlined" sx={{ borderRadius: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 1.5, sm: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }}>
          <IconButton onClick={() => navigate('/projects')} size="small" sx={{ mr: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ flex: 1 }}>
            {STEPS.map((label, i) => {
              const completed = i < activeStep;
              const active = i === activeStep;
              const fillRatio = getStepFillRatio(i);
              const isValid = fillRatio === 1;
              const statusColor = fillRatio === 1 ? 'success.main' : fillRatio === 0 ? 'error.main' : 'warning.main';
              const iconEl = STEP_ICON_MAP[i + 1] || <CheckCircleIcon />;
              let bgColor = 'action.disabledBackground', iconColor: string = 'text.secondary';
              if (completed) { bgColor = statusColor; iconColor = '#fff'; }
              else if (active) { bgColor = 'primary.main'; iconColor = '#fff'; }
              const textColor = completed ? statusColor : active ? 'primary.main' : 'text.secondary';
              return (
                <Step key={label} completed={completed && isValid}>
                  <StepLabel onClick={() => handleStepClick(i)} sx={{ cursor: 'pointer' }}
                    StepIconComponent={() => (
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bgColor, color: iconColor, boxShadow: active ? 3 : 0 }}>
                        <Box sx={{ display: 'flex', fontSize: 16, '& > svg': { fontSize: 'inherit' } }}>{iconEl}</Box>
                      </Box>
                    )}>
                    <Typography variant="caption" fontWeight={active ? 700 : 500} sx={{ color: textColor }}>{label}</Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
          </Box>
          {formError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1, flexShrink: 0 }}>{formError}</Alert>}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>{stepContent[activeStep]()}</Box>
          <Divider sx={{ mt: 1.5, mb: 1.5, flexShrink: 0 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
            <Button onClick={() => navigate('/')} color="inherit">{t('onboarding.cancel')}</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep > 0 && <Button onClick={handleBack}>{t('onboarding.back')}</Button>}
              {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={handleNext}>{t('onboarding.next')}</Button>}
              {activeStep === STEPS.length - 1 && (
                <Button variant="contained" onClick={handleSubmit} disabled={submitting || !allStepsValid}
                  startIcon={submitting ? <NerdSpinner size={18} /> : <CheckCircleIcon />}>
                  {submitting ? 'Submitting…' : t('onboarding.submit')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
      <Snackbar open={!!snackMsg} autoHideDuration={3000} onClose={() => setSnackMsg('')} message={snackMsg} />
    </Box>
  );
};

export default OnboardingWizard;
